/**
 * The Manpower calculation engine's own master data and DTOs: Effort_Assumption
 * (`/api/manpower/effort-assumptions`), Capacity_Period
 * (`/api/manpower/capacity-periods`) and the calculation-chain response shapes
 * Requirement 5.8's tooltip and Requirement 6.5's drill-through need
 * (`complan-manpower/requirements.md`, Requirements 2, 3, 5, 6).
 *
 * Region and POS_Segment are not here: they are ordinary members of
 * `MASTER_DATA_SETS` (`enums.ts`) and serve through `admin.ts`'s existing
 * `masterDataWriteSchema`/`posSegmentWriteSchema` shapes, exactly as `platform`
 * and `priority` do. Effort_Assumption and Capacity_Period do not fit that
 * shape - each is keyed by a compound combination plus an effective date or
 * period rather than a single `code` (the same reason `sales_cycle.ts` is its
 * own file rather than an `admin.ts` extension) - so they get their own module.
 *
 * The demand-input fields on `initiative_ff_scope` are not here either: they
 * extend `initiativeFieldForceScopeInputSchema`/`initiativeFieldForceScopeSchema`
 * in `initiative.ts` directly, because they are columns of that existing scope
 * entry (`complan-manpower/design.md`, "Field Force Demand Drivers... is columns
 * on `initiative_ff_scope`, not a new table"), not a separate resource.
 *
 * ## Exact-decimal fields, not doubles
 *
 * Every `numeric` column this module's schemas describe -
 * effort-per-unit-minutes, fixed overhead hours, the productive-hours and
 * availability-factor assumptions - is expressed with
 * {@link decimalTextSchema}: canonical decimal text parsed and range-checked
 * over exact rationals (`@complan/domain`'s `exact-fraction.ts`), never a
 * `number`. This is the wire-boundary half of the same correction
 * `exact-fraction.ts`'s own module comment documents for the Demand_Engine's
 * internal arithmetic: a value that enters the domain already as an imprecise
 * double could reintroduce the rounding error the internal arithmetic was
 * built to avoid, so the boundary has to hold the line too.
 */
import { z } from 'zod';
import { fieldForceRoleSchema } from './enums.js';
import { AVAILABILITY_FACTOR_MAX, AVAILABILITY_FACTOR_MIN, CAPACITY_PERIOD_CHANGE_REASON_MAX_LENGTH, CAPACITY_PERIOD_CHANGE_REASON_MIN_LENGTH, CAPACITY_THRESHOLD_MAX, CAPACITY_THRESHOLD_MIN, EFFORT_PER_UNIT_MINUTES_MAX, EFFORT_PER_UNIT_MINUTES_MIN, FIXED_OVERHEAD_HOURS_MAX, FIXED_OVERHEAD_HOURS_MIN, PRODUCTIVE_HOURS_PER_WORKDAY_MAX, PRODUCTIVE_HOURS_PER_WORKDAY_MIN, } from './limits.js';
import { decimalTextSchema, idSchema, pagedResponseSchema, planningDateSchema, ruleIdSchema, trimmedTextSchema, } from './primitives.js';
/** `/api/manpower/effort-assumptions/:id`, `/api/manpower/capacity-periods/:id`. */
export const manpowerRecordIdParamsSchema = z.object({ id: idSchema });
/**
 * An Effort_Assumption version as read back (R2.1, R2.2, R2.6).
 *
 * `version` and `isActive` are read-only: a "version" is an integer the service
 * assigns as one more than the highest existing version of the combination, and
 * `isActive` is toggled by the deactivation action, never by this shape's own
 * write path.
 */
export const effortAssumptionSchema = z.object({
    id: idSchema,
    activityTypeId: idSchema,
    ffRole: fieldForceRoleSchema,
    populationUnitId: idSchema,
    effortPerUnitMinutes: decimalTextSchema({
        min: EFFORT_PER_UNIT_MINUTES_MIN,
        max: EFFORT_PER_UNIT_MINUTES_MAX,
        decimalPlaces: 2,
    }),
    fixedOverheadHours: decimalTextSchema({
        min: FIXED_OVERHEAD_HOURS_MIN,
        max: FIXED_OVERHEAD_HOURS_MAX,
        decimalPlaces: 2,
    })
        .nullable()
        .default(null),
    effectiveFrom: planningDateSchema,
    version: z.number().int().min(1),
    isActive: z.boolean(),
});
/**
 * `POST /api/manpower/effort-assumptions` (R2.1, R2.2, R2.5, R2.6, R2.7).
 *
 * Every submission is a new version, never an edit of an existing row (R2.2):
 * there is no `PUT`/`PATCH` endpoint or schema for an existing Effort_Assumption
 * row's calculation-bearing columns. R2.5's mandatory recalculation warning is
 * acknowledged the same way Foundation's own mandatory-warning flows are - the
 * `acknowledgedWarningRuleIds` field carries the rule identifier the client
 * confirmed, and the service refuses to persist without it.
 */
export const effortAssumptionWriteSchema = z.object({
    activityTypeId: idSchema,
    ffRole: fieldForceRoleSchema,
    populationUnitId: idSchema,
    effortPerUnitMinutes: decimalTextSchema({
        min: EFFORT_PER_UNIT_MINUTES_MIN,
        max: EFFORT_PER_UNIT_MINUTES_MAX,
        decimalPlaces: 2,
    }),
    fixedOverheadHours: decimalTextSchema({
        min: FIXED_OVERHEAD_HOURS_MIN,
        max: FIXED_OVERHEAD_HOURS_MAX,
        decimalPlaces: 2,
    })
        .nullable()
        .default(null),
    effectiveFrom: planningDateSchema,
    acknowledgedWarningRuleIds: z.array(ruleIdSchema).default([]),
});
/**
 * `POST /api/manpower/effort-assumptions/:id/deactivate` (R2.8).
 *
 * Deactivation is its own action rather than a field on the write schema,
 * matching Foundation's own master-data deactivation shape (`admin.ts`'s
 * `masterDataWriteSchema.isActive`, toggled through the same write path a
 * create uses) is deliberately *not* followed here: an Effort_Assumption
 * version is never re-submitted through {@link effortAssumptionWriteSchema}
 * once created (R2.2), so there is no write call to carry an `isActive: false`
 * on. R2.8's deactivation-blocked outcome is reported the same way Foundation's
 * master-data deletion refusal is (component 2 of design.md): the `refused`
 * shape of a delete-style outcome, carrying the blocking reference count.
 */
export const deactivateEffortAssumptionRequestSchema = z.object({
    acknowledgedWarningRuleIds: z.array(ruleIdSchema).default([]),
});
/**
 * `GET /api/manpower/effort-assumptions`. Every version of every combination,
 * so the Effort Matrix screen (R7.4) can group and page them client-side; the
 * population at Manpower's volumetric baseline (at most 50 active combinations,
 * R9.3) is small enough that a single unpaged read is the simpler contract.
 */
export const effortAssumptionListResponseSchema = z.object({
    items: z.array(effortAssumptionSchema).default([]),
});
/**
 * A Capacity_Period version as read back (R3.1, R3.2, R3.4).
 *
 * `headcount` is nullable: a Capacity_Period recorded with no headcount value
 * is R3.4's Data_Incomplete case, not a validation failure - the row is a
 * legitimate placeholder for a scope whose headcount is not yet agreed.
 */
export const capacityPeriodSchema = z.object({
    id: idSchema,
    ffRole: fieldForceRoleSchema,
    regionId: idSchema,
    headcount: z.number().int().min(0).nullable().default(null),
    workdays: z.number().int().min(0),
    oofDays: z.number().int().min(0),
    productiveHoursPerWorkday: decimalTextSchema({
        min: PRODUCTIVE_HOURS_PER_WORKDAY_MIN,
        max: PRODUCTIVE_HOURS_PER_WORKDAY_MAX,
        decimalPlaces: 2,
    }),
    availabilityFactor: decimalTextSchema({
        min: AVAILABILITY_FACTOR_MIN,
        max: AVAILABILITY_FACTOR_MAX,
        decimalPlaces: 2,
    }),
    effectivePeriodStart: planningDateSchema,
    effectivePeriodEnd: planningDateSchema,
    version: z.number().int().min(1),
    isActive: z.boolean(),
    changeReason: trimmedTextSchema(CAPACITY_PERIOD_CHANGE_REASON_MIN_LENGTH, CAPACITY_PERIOD_CHANGE_REASON_MAX_LENGTH),
});
/**
 * `POST /api/manpower/capacity-periods` (R3.1, R3.2, R3.3, R3.6, R3.7).
 *
 * `changeReason` is required on every submission, matching migration 18's own
 * rule (see that migration's header comment): there is no meaningful
 * distinction between "creating" and "editing" a Capacity_Period, since even
 * the first entry for a period is a change from "no capacity planned" to a
 * stated number. `headcount` is omittable (R3.4's Data_Incomplete case is a
 * legitimate submission, not a rejection); `oofDays` must not exceed
 * `workdays`, checked by `superRefine` here so the common violation is reported
 * before the round trip that would otherwise raise it as the database
 * `capacity_period_oof_not_exceeding_workdays` constraint.
 */
export const capacityPeriodWriteSchema = z
    .object({
    ffRole: fieldForceRoleSchema,
    regionId: idSchema,
    headcount: z.number().int().min(0).nullable().default(null),
    workdays: z.number().int().min(0),
    oofDays: z.number().int().min(0),
    productiveHoursPerWorkday: decimalTextSchema({
        min: PRODUCTIVE_HOURS_PER_WORKDAY_MIN,
        max: PRODUCTIVE_HOURS_PER_WORKDAY_MAX,
        decimalPlaces: 2,
    }),
    availabilityFactor: decimalTextSchema({
        min: AVAILABILITY_FACTOR_MIN,
        max: AVAILABILITY_FACTOR_MAX,
        decimalPlaces: 2,
    }),
    effectivePeriodStart: planningDateSchema,
    effectivePeriodEnd: planningDateSchema,
    changeReason: trimmedTextSchema(CAPACITY_PERIOD_CHANGE_REASON_MIN_LENGTH, CAPACITY_PERIOD_CHANGE_REASON_MAX_LENGTH),
    acknowledgedWarningRuleIds: z.array(ruleIdSchema).default([]),
})
    .superRefine((data, ctx) => {
    if (data.oofDays > data.workdays) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['oofDays'],
            params: { key: 'capacityPeriod.oofDaysExceedWorkdays' },
        });
    }
});
/**
 * `PUT /api/manpower/capacity-periods/:id` (R3.3, R3.6, R3.7).
 *
 * The body of an *edit* of an existing Capacity_Period, and deliberately not
 * {@link capacityPeriodWriteSchema}: it carries no `ffRole`, `regionId`,
 * `effectivePeriodStart` or `effectivePeriodEnd`.
 *
 * R3.1 keys a Capacity_Period by FF role, Region and period, and R3.3 names the
 * fields an edit changes - "headcount, workdays, OOF days, productive-hours
 * assumption or availability factor" - which is exactly the set below. The scope
 * is therefore the record's identity, not part of its payload: the server reads
 * it from the record named by `:id`, so an edit can never silently re-point a
 * period at another role, Region or date range and leave the original standing
 * as a duplicate. Correcting a mis-entered scope is a new Capacity_Period plus a
 * deactivation of the wrong one, not an edit.
 *
 * `changeReason` is required (R3.3's 1-to-500-character reason) and
 * `acknowledgedWarningRuleIds` carries R3.6's acknowledgement plus, when the
 * edited figures leave the period short of its already-planned demand, the
 * capacity-shortfall acknowledgement - which is a warning, not a block: reducing
 * capacity below planned demand is a legitimate thing to record, and the
 * resulting over-capacity state is what the utilization screens exist to report.
 */
export const capacityPeriodEditSchema = z
    .object({
    headcount: z.number().int().min(0).nullable().default(null),
    workdays: z.number().int().min(0),
    oofDays: z.number().int().min(0),
    productiveHoursPerWorkday: decimalTextSchema({
        min: PRODUCTIVE_HOURS_PER_WORKDAY_MIN,
        max: PRODUCTIVE_HOURS_PER_WORKDAY_MAX,
        decimalPlaces: 2,
    }),
    availabilityFactor: decimalTextSchema({
        min: AVAILABILITY_FACTOR_MIN,
        max: AVAILABILITY_FACTOR_MAX,
        decimalPlaces: 2,
    }),
    changeReason: trimmedTextSchema(CAPACITY_PERIOD_CHANGE_REASON_MIN_LENGTH, CAPACITY_PERIOD_CHANGE_REASON_MAX_LENGTH),
    acknowledgedWarningRuleIds: z.array(ruleIdSchema).default([]),
})
    .superRefine((data, ctx) => {
    if (data.oofDays > data.workdays) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['oofDays'],
            params: { key: 'capacityPeriod.oofDaysExceedWorkdays' },
        });
    }
});
/** `GET /api/manpower/capacity-periods`, at Manpower's volumetric baseline (R9.3), unpaged for the same reason {@link effortAssumptionListResponseSchema} is. */
export const capacityPeriodListResponseSchema = z.object({
    items: z.array(capacityPeriodSchema).default([]),
});
/**
 * The Data_Incomplete sentinel a calculated value carries when it could not be
 * produced (R3.4, R5.9, R6.3): no resolvable Effort_Assumption, no headcount on
 * the applicable Capacity_Period, or a Capacity_Hours of zero. Every
 * `*DtoSchema` below carries it as a discriminant alongside the numeric value
 * rather than relying on `null` alone, so a client can render the specific
 * Data_Incomplete indicator R3.4/R5.9/R6.3 each require instead of guessing at
 * the reason for an absent number.
 */
/** The four Bottleneck_State values of R6.4. */
export const BOTTLENECK_STATES = Object.freeze([
    'normal',
    'warning',
    'over_capacity',
    'data_incomplete',
]);
/** See {@link BOTTLENECK_STATES}. */
export const bottleneckStateSchema = z.enum(BOTTLENECK_STATES);
/**
 * The calculation chain of a Demand_Hours value (R5.8): population, coverage,
 * frequency, effort-per-unit, fixed overhead, the applicable Effort_Assumption
 * version and the period allocation, exposed for the formula-transparency
 * tooltip UI spec 5.5 requires and every Demand_Hours-presenting screen
 * carries alongside the number itself.
 *
 * Every quantity here is a plain rendered number (rounded for display,
 * Assumption A11), not the {@link DecimalText}/`ExactFraction` the engine
 * computes internally - this is the display boundary the internal exact
 * arithmetic hands off to, once and only once.
 */
export const demandCalculationChainSchema = z.object({
    driverId: idSchema,
    impactedPopulationCount: z.number(),
    coverageFactor: z.number(),
    frequency: z.number().int().min(1),
    effortAssumptionId: idSchema,
    effortAssumptionVersion: z.number().int().min(1),
    effortPerUnitMinutes: z.number(),
    fixedOverheadHours: z.number().nullable().default(null),
    demandHours: z.number(),
});
/** One Demand_Hours value, Data_Incomplete when no Effort_Assumption resolved (R5.9). */
export const demandHoursDtoSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('calculated') }).merge(demandCalculationChainSchema),
    z.object({
        status: z.literal('data_incomplete'),
        driverId: idSchema,
        /** The missing Activity_Type/FF role/population unit combination R5.9 names in its finding. */
        missingActivityTypeId: idSchema,
        missingFfRole: fieldForceRoleSchema,
        missingPopulationUnitId: idSchema,
    }),
]);
/** One Capacity_Hours value, Data_Incomplete when the applicable Capacity_Period holds no headcount (R3.4). */
export const capacityHoursDtoSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('calculated'),
        capacityPeriodId: idSchema,
        headcount: z.number().int().min(0),
        workdays: z.number().int().min(0),
        oofDays: z.number().int().min(0),
        productiveHoursPerWorkday: z.number(),
        availabilityFactor: z.number(),
        capacityHours: z.number(),
    }),
    z.object({
        status: z.literal('data_incomplete'),
        capacityPeriodId: idSchema.nullable().default(null),
    }),
]);
/**
 * A Utilization value and its Bottleneck_State classification (R6.2, R6.3,
 * R6.4, R6.5).
 *
 * `excessHours` is present only for `over_capacity` (R6.5); `utilization` is
 * `null` exactly when `bottleneckState` is `'data_incomplete'` (R6.3) - the same
 * pairing `classifyBottleneck` in `@complan/domain` returns, carried here
 * unchanged onto the wire.
 */
export const utilizationDtoSchema = z.object({
    ffRole: fieldForceRoleSchema,
    regionId: idSchema.nullable().default(null),
    periodId: z.string().min(1),
    demandHours: z.number(),
    capacityHours: z.number().nullable().default(null),
    utilization: z.number().nullable().default(null),
    bottleneckState: bottleneckStateSchema,
    excessHours: z.number().nullable().default(null),
    capacityThreshold: z.number().int().min(CAPACITY_THRESHOLD_MIN).max(CAPACITY_THRESHOLD_MAX),
});
/** A page of contributing Field_Force_Demand_Drivers, ordered by descending Demand_Hours contribution (R6.5's drill-through). */
export const contributingDriverItemSchema = z.object({
    driverId: idSchema,
    initiativeId: idSchema,
    initiativeRef: z.string(),
    title: z.string(),
    demandHours: z.number(),
});
/** `GET /api/manpower/utilization/:scope/contributing-drivers` (R6.5). */
export const contributingDriverListResponseSchema = pagedResponseSchema(contributingDriverItemSchema);
//# sourceMappingURL=manpower.js.map